import { useState, type ReactNode } from "react";
// Pill tracking + popup storico colli: gli stessi dei Messaggi (design B
// 2026-07-02, Davide: "il tracking come lo vedo nei messaggi").
import { TrackingHistoryDialog } from "@/features/messaggi/components/TrackingHistoryDialog";
import { TrackingTimeline } from "@/features/messaggi/components/TrackingTimeline";
import { openExternalUrl } from "@/shared/openExternalUrl";
import { useClipboardFeedback } from "@/shared/useClipboardFeedback";
import { fetchOrderParcelCodes } from "../api";
import type { Order } from "../types";
import { IconCheck, IconCopy, IconHistory } from "./icons";
import { coloriCorriere, FORMA_BADGE, pieno } from "./statoToni";

/** Colonna SPEDIZIONE della card lista (Davide 2026-09-23: «migliora la parte
 *  spedizione»). Tre piani, dal piu' generale al piu' preciso: chi porta il
 *  pacco (corriere, colli, scadenza), il codice da copiare, lo stato del
 *  corriere. Sotto, se c'e', la SOSTITUZIONE: un altro pacco, col suo codice.
 *
 *  ``scadenza`` e' uno slot: il chip deadline vive in ``OrderCard`` accanto al
 *  bordo rosso «da spedire oggi», che usa la stessa regola. */
export function SpedizioneCell({ order, scadenza }: { order: Order; scadenza?: ReactNode }) {
  const trackingCode = orderTrackingCode(order);
  const [sostituzione, ...precedenti] = order.replacementTracking ?? [];
  return (
    <div className="min-w-0 flex flex-col justify-center gap-[5px]">
      <div className="flex min-w-0 items-center gap-1.5">
        {order.corriere ? (
          <span
            style={coloriCorriere(order.corriere) ?? undefined}
            className={`${FORMA_BADGE} border-line-2 bg-card font-semibold text-ink`}
          >
            {order.corriere}
          </span>
        ) : (
          <span className="text-[11px] text-ink">—</span>
        )}
        {order.colli > 0 && (
          <span className="whitespace-nowrap text-[11px] tabular-nums text-ink">
            {order.colli} {order.colli === 1 ? "collo" : "colli"}
          </span>
        )}
        {scadenza && <span className="ml-auto flex shrink-0">{scadenza}</span>}
      </div>
      {trackingCode ? (
        <>
          {/* Il codice e' neutro: il colore lo porta lo stato del corriere sotto. */}
          <TrackingCopyPill code={trackingCode} hasEvents={order.lastTrackingEvent != null} />
          <TrackingTimeline
            statusRaw={order.status}
            carrier={order.corriere || ""}
            trackingCode={trackingCode}
            parcels={[]}
            onOpenExternal={openExternalUrl}
            variant="dot"
            loadParcelCodes={() => fetchOrderParcelCodes(order.id)}
            lastCourierEvent={order.lastTrackingEvent ?? null}
          />
        </>
      ) : (
        // Tratteggiato: e' il posto del codice, ancora vuoto.
        <span className={`${FORMA_BADGE} self-start border-dashed border-line-2 bg-card text-ink`}>
          Nessun tracking
        </span>
      )}
      {sostituzione && <SostituzioneTracking codice={sostituzione} precedenti={precedenti} />}
    </div>
  );
}

/** Il tracking della SOSTITUZIONE (``returns.returns``): il pezzo nuovo
 *  rimandato al cliente. Si vede la piu' recente; le altre stanno nel «+N».
 *
 *  ⚠️ Il codice sopra resta la spedizione ORIGINALE: sono due pacchi, e dare al
 *  cliente quello sbagliato lo manda a cercare il pacco gia' arrivato. */
function SostituzioneTracking({ codice, precedenti }: { codice: string; precedenti: string[] }) {
  const [storicoAperto, setStoricoAperto] = useState(false);
  return (
    <div
      data-testid="sostituzione-tracking"
      className="flex min-w-0 items-center gap-1 border-t border-dashed border-line-2 pt-[5px]"
    >
      {/* L'indaco del badge «Sost. ×N» della colonna Stati: e' la stessa cosa. */}
      <span
        title="Spedizione della sostituzione: il pezzo rimandato al cliente"
        style={pieno("sost-fornitore")}
        className={FORMA_BADGE}
      >
        Sost.
      </span>
      <TrackingCopyPill code={codice} label="Copia tracking della sostituzione" />
      {precedenti.length > 0 && (
        <span
          title={`Sostituzioni precedenti: ${precedenti.join(", ")}`}
          className="shrink-0 cursor-help text-[10px] font-medium text-ink"
        >
          +{precedenti.length}
        </span>
      )}
      <button
        type="button"
        data-no-open
        aria-label="Apri lo storico della sostituzione"
        title="Apri lo storico della sostituzione"
        onClick={(e) => {
          e.stopPropagation();
          setStoricoAperto(true);
        }}
        className="ml-auto inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md text-ink transition-colors hover:bg-paper-2 hover:text-ink"
      >
        <IconHistory className="h-3 w-3 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
      </button>
      {storicoAperto && (
        // ponytail: le sostituzioni escono SOLO come LDV SDA (``returns``,
        // servizio S09): il corriere e' quello. Se ne arriva un altro, serve
        // la colonna corriere su ``returns.returns``.
        <TrackingHistoryDialog
          carrier="SDA"
          trackingCode={codice}
          parcelCodes={[codice]}
          onOpenExternal={openExternalUrl}
          onClose={() => setStoricoAperto(false)}
        />
      )}
    </div>
  );
}

/** Codice tracking dell'ordine, letto dal raw Poleepo (il tipo Order non lo
 *  espone come campo top-level). Tollerante: prova ``tracking_code`` e, in
 *  fallback, ``tracking_number``. Ritorna ``null`` se assente/non-stringa, cosi'
 *  il segmento info di destra non mostra rumore "--". */
function orderTrackingCode(order: Order): string | null {
  // Fonte primaria: colonna locale Order.tracking_code (scritta da Genera
  // LDV) — nel DB reale il raw Poleepo quasi mai contiene il codice
  // (verifica live 2026-07-03: 124/200 ordini solo top-level, 0 nel raw).
  const top = (order.trackingCode ?? "").trim();
  if (top) return top;
  const raw = order.raw;
  const code = raw?.tracking_code ?? raw?.tracking_number;
  if (typeof code === "string") {
    const trimmed = code.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
}

/** Numero spedizione copiabile in colonna SPEDIZIONE (W1 item 1): click →
 *  copia con feedback. Mono, troncato, col glifo «copia» a destra.
 *
 *  NEUTRO dal 2026-09-23 (Davide: «tutti i colori nello stile delle etichette
 *  di stato»): il codice non e' uno stato. Il verde «il corriere ha eventi»
 *  (2026-08-13) e' passato all'etichetta dello stato del corriere, subito
 *  sotto; qui ``hasEvents`` cambia solo il titolo. */
export function TrackingCopyPill({
  code,
  hasEvents = false,
  label = "Copia numero spedizione",
}: {
  code: string;
  hasEvents?: boolean;
  /** Cosa si copia: la sostituzione ha un codice diverso dall'originale, e
   *  il lettore di schermo deve saperlo quanto l'occhio. */
  label?: string;
}) {
  const { copied, copy } = useClipboardFeedback(1200);
  const title = hasEvents
    ? "Il corriere ha registrato movimenti su questo codice — clicca per copiarlo"
    : label;
  return (
    <button
      type="button"
      data-no-open
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        void copy(code);
      }}
      className={[
        // self-start: nel flex-col della colonna Spedizione il default
        // (stretch) allargava la pill a tutta la colonna (Davide 2026-07-14).
        "group inline-flex min-w-0 items-center gap-1 self-start w-fit max-w-full rounded-md border-[1.5px] bg-card px-1.5 py-px text-[10.5px] shrink-0 transition-colors",
        copied ? "border-green text-green" : "border-line-2 text-ink hover:border-ink-4",
      ].join(" ")}
    >
      <span className="truncate">{copied ? "Copiato!" : code}</span>
      {copied ? (
        <IconCheck className="h-[9px] w-[9px] shrink-0 fill-none stroke-current stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]" />
      ) : (
        <IconCopy className="h-[9px] w-[9px] shrink-0 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
      )}
    </button>
  );
}
